import { z } from 'zod';
// The building · floor · apartment navigator (U3, docs/product-ux 07, 08). Every status is computed
// by the server from stored data; the client only presents it. No personal data: no resident,
// contact or note field exists here.
/**
 * The handover stage of an apartment (08 §1.1, in order of precedence §2). There is no
 * "delivered": a fully checked handover is "summarized, not signed" until a signing process
 * exists. `unavailable`: the reader may not see handovers.
 */
export const HANDOVER_STAGES = [
    'unavailable',
    'not_started',
    'scheduled',
    'initial_in_progress',
    'initial_summary',
    'final_in_progress',
    'final_summary',
];
/**
 * Data problems shown, never hidden (08 §1.3, §2): missing Master Data, and states that contradict
 * each other (a final handover without an initial one, two drafts of one type).
 */
export const APARTMENT_EXCEPTIONS = [
    'missing_model_type',
    'missing_floor',
    'inconsistent_state',
];
export const navigatorApartmentSchema = z
    .object({
    id: z.uuid(),
    /** Null: the apartment has no floor (a "missing_floor" exception). */
    levelId: z.uuid().nullable(),
    /** The current (displayed) apartment number. */
    number: z.string(),
    /** The original number in the source, and the floor as the source states it (DR-183). */
    sourceNumber: z.string().nullable(),
    sourceFloorLabel: z.string().nullable(),
    modelType: z.string().nullable(),
    /** Parking and storage spaces allocated to it now. */
    parking: z.number().int().min(0),
    storage: z.number().int().min(0),
    stage: z.enum(HANDOVER_STAGES),
    /**
     * Open findings (a finding repaired at reinspection no longer counts) and initial findings the
     * final handover has not reinspected yet; null with stage `unavailable`.
     */
    defects: z
        .object({ open: z.number().int().min(0), awaiting: z.number().int().min(0) })
        .strict()
        .nullable(),
    exceptions: z.array(z.enum(APARTMENT_EXCEPTIONS)),
})
    .strict();
export const navigatorLevelSchema = z
    .object({ id: z.uuid(), label: z.string(), number: z.number().int() })
    .strict();
export const navigatorBuildingSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    /** Every floor, top first (the stored display order), also floors without apartments. */
    levels: z.array(navigatorLevelSchema),
    /** In sheet order: floor, then natural apartment number. */
    apartments: z.array(navigatorApartmentSchema),
})
    .strict();
/** A parking or storage space allocated to no apartment (free, or held while disputed). */
export const unassignedSpaceSchema = z
    .object({
    kind: z.enum(['parking', 'storage']),
    number: z.string(),
    location: z.string(),
})
    .strict();
/** `GET /projects/:projectId/navigator`. */
export const navigatorResponseSchema = z
    .object({
    project: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).strict(),
    /** Buildings that hold apartments, or are residential, in display order. */
    buildings: z.array(navigatorBuildingSchema),
    unassignedSpaces: z.array(unassignedSpaceSchema),
    permissions: z
        .object({
        handoverView: z.boolean(),
        /** May add an apartment (projects.unit.create): the phone's "הוספת דירה" per floor. */
        unitCreate: z.boolean(),
        /** May add buildings and floors (projects.structure.edit). */
        structureEdit: z.boolean(),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=navigator.js.map